import { useMemo, type Dispatch, type RefObject, type SetStateAction } from 'react';
import type { FormInstance } from 'antd';
import type { SceneSetVerdict, TaskListParams } from '@apis/simulation';
import { EMPTY_COMMENT_FILTER, type CommentFilterValue } from '@components/CommentFilterPopover';
import SceneSetStats from './SceneSetStats';
import { useSceneSetStats } from './useSceneSetStats';
import { sceneSetOptions } from './useSceneSetComparison';
import { statsDefaultStatus, toggleSceneSetDrill } from '../sceneSetFilter';

interface SceneSetDrillDeps {
  jobId: number;
  compareMode: boolean;
  diffFilter: { compareJobId?: number; diffMode?: unknown };
  statsMode: boolean;
  statsTaskStatus: number | undefined;
  queryParams: TaskListParams;
  form: FormInstance;
  setQueryParams: Dispatch<SetStateAction<TaskListParams>>;
  setEvalCellFilter: (value: null) => void;
  setCommentFilter: Dispatch<SetStateAction<CommentFilterValue>>;
  syncTaskFiltersToUrl: (params: TaskListParams) => void;
  loadTaskList: (params: TaskListParams) => Promise<void>;
  loadFilteredCount: (params: TaskListParams) => Promise<void>;
  taskDetailRef: RefObject<HTMLDivElement | null>;
}

/** 单 Job「按场景集统计」+ 场景集筛选/下钻的接线（页面只消费返回值）。 */
export function useSceneSetDrill(deps: SceneSetDrillDeps) {
  const {
    jobId, compareMode, diffFilter, statsMode, statsTaskStatus, queryParams, form,
    setQueryParams, setEvalCellFilter, setCommentFilter, syncTaskFiltersToUrl,
    loadTaskList, loadFilteredCount, taskDetailRef,
  } = deps;
  // 后端禁止 sceneSetId 与 compareJobId 同传，故对比页与 Daily 差异下钻（URL 上的
  // compareJobId+diffMode）下都不展示、不请求、不发 sceneSetId。
  // 基于同步可得的 diffFilter 而非 queryParams，避免首帧误发后再 abort。
  const sceneSetFilterAllowed = !compareMode && !(diffFilter.compareJobId && diffFilter.diffMode);
  const sceneSetStats = useSceneSetStats(jobId, sceneSetFilterAllowed);
  const jobSceneSetOptions = useMemo(() => sceneSetOptions(sceneSetStats.nodes ?? []), [sceneSetStats.nodes]);

  const applyParams = (newParams: TaskListParams) => {
    setQueryParams(newParams);
    syncTaskFiltersToUrl(newParams);
    void loadTaskList(newParams);
    void loadFilteredCount(newParams);
  };

  // 数字下钻：清空其它明细筛选（含默认 ?taskStatus=），只按场景集 + 判定取每场景最新 task，
  // 保证行数 = 格子数字。再次点同一格取消。
  const handleSceneSetStatsSelect = (nodeId: number, verdict: SceneSetVerdict) => {
    const next = toggleSceneSetDrill(
      { sceneSetId: queryParams.sceneSetId, sceneSetVerdict: queryParams.sceneSetVerdict },
      nodeId, verdict,
    );
    // 下钻时不叠加统计页默认 taskStatus；取消下钻时恢复（taskStatus 留在 URL 上）。
    const status = statsDefaultStatus(statsMode, statsTaskStatus, next);
    form.resetFields();
    form.setFieldsValue({ status });
    setEvalCellFilter(null);
    setCommentFilter(EMPTY_COMMENT_FILTER);
    applyParams({
      jobId,
      current: 1,
      pageSize: queryParams.pageSize || 10,
      ...(status !== undefined ? { status } : {}),
      ...next,
    });
    taskDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 手动场景集树筛选：与其它表单筛选叠加；改选即清掉判定下钻。
  const handleSceneSetTreeChange = (value: number | undefined) => {
    applyParams({
      ...queryParams,
      sceneSetId: value ?? undefined,
      sceneSetVerdict: undefined,
      current: 1,
    });
  };

  // 与对比页「按场景集对比」同款：挂在页头统计卡片底部（分隔线 + 折叠头），不单独成卡。
  const sceneSetStatsNode = sceneSetFilterAllowed ? (
    <SceneSetStats
      key={jobId}
      {...sceneSetStats}
      onSelect={handleSceneSetStatsSelect}
      selectedNodeId={queryParams.sceneSetId}
      selectedVerdict={queryParams.sceneSetVerdict}
    />
  ) : null;

  return { sceneSetFilterAllowed, sceneSetStats, jobSceneSetOptions, handleSceneSetTreeChange, sceneSetStatsNode };
}
